/// <reference types="vite/client" />
// frontend/src/core/apiClient.ts
// fetch-обгортка до Worker API
// Base URL береться з VITE_API_URL env або з проксі (порожній рядок для dev)

import type { ApiError } from './types.ts';

/**
 * Кастомна помилка API з HTTP статусом і body
 */
export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError | string
  ) {
    const message = typeof body === 'string' ? body : body.error;
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Базовий URL API.
 * В dev: '' (порожній, проксі Vite перехоплює /api → localhost:8787)
 * В prod: https://your-worker.workers.dev
 */
const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  path: string,
  body?: unknown
): Promise<T> {
  const url = `${BASE_URL}${path}`;

  const isFormData = body instanceof FormData;

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    method,
    headers,
    body: isFormData ? (body as FormData) : (body !== undefined ? JSON.stringify(body) : undefined),
  });

  if (!response.ok) {
    let errorBody: ApiError | string;
    try {
      errorBody = (await response.json()) as ApiError;
    } catch {
      errorBody = await response.text();
    }
    throw new ApiClientError(response.status, errorBody);
  }

  // 204 No Content — повертаємо порожній об'єкт
  if (response.status === 204) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Типізований API клієнт
 * @example
 * const accounts = await api.get<SocialAccount[]>('/api/accounts');
 * await api.delete('/api/accounts/123');
 */
export const api = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body),
  delete: <T>(path: string): Promise<T> => request<T>('DELETE', path),
};
